import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

const formatFecha = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    return `${String(date.getDate()).padStart(2, '0')} de ${meses[date.getMonth()]} del ${date.getFullYear()}`;
};

const ph = (value, label) => {
    if (!value || String(value).trim() === "") return `[${label}]`;
    return value;
};

// Formatea un RUT chileno con puntos y guion (12345678K -> 12.345.678-K).
// Si el cuerpo no es numérico (ej. un DNI extranjero o un placeholder), devuelve el valor sin tocar.
const formatRut = (rut) => {
    if (!rut) return rut;
    const clean = String(rut).replace(/[.\-\s]/g, '').toUpperCase();
    if (clean.length < 2) return rut;
    const body = clean.slice(0, -1);
    const dv = clean.slice(-1);
    if (!/^\d+$/.test(body)) return rut;
    const bodyFmt = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${bodyFmt}-${dv}`;
};

const b = (t) => ({ text: t, bold: true });
const bu = (t) => ({ text: t, bold: true, decoration: 'underline' });

// Layout estándar para las tablas de jornada (cuadrícula completa, paddings parejos)
const standardTableLayout = {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => '#888',
    vLineColor: () => '#888',
    paddingLeft: () => 6,
    paddingRight: () => 6,
    paddingTop: () => 4,
    paddingBottom: () => 4,
};

// ----- Jornada por defecto (precargada desde el contrato vigente, editable en el formulario) -----
export const TURNO_DIA_DEFAULT = [
    "Lunes, miércoles y viernes: desde 09:00 hrs hasta 18:00 hrs",
    "Martes y jueves: desde 09:00 hrs hasta 17:00 hrs",
    "Sábado: desde 09:00 hrs hasta 13:00 hrs",
];

export const TURNO_NOCHE_DEFAULT = [
    { entranteDia: "Lunes", entranteHora: "20:00", salienteDia: "Martes", salienteHora: "06:00" },
    { entranteDia: "Martes", entranteHora: "20:00", salienteDia: "Miércoles", salienteHora: "05:00" },
    { entranteDia: "Miércoles", entranteHora: "20:00", salienteDia: "Jueves", salienteHora: "06:00" },
    { entranteDia: "Jueves", entranteHora: "20:00", salienteDia: "Viernes", salienteHora: "05:00" },
    { entranteDia: "Viernes", entranteHora: "20:00", salienteDia: "Sábado", salienteHora: "05:00" },
];

/**
 * Genera el PDF del contrato de trabajo.
 *
 * @param {Object} params
 * @param {Object} params.formData        Datos del contrato
 * @param {Array}  params.funciones       Listado de funciones del cargo
 * @param {Array}  params.turnoDia        Líneas del turno de día (Lun-Sáb)
 * @param {Array}  params.turnoNoche      Filas de rotación del turno de noche
 * @param {string} params.logoBase64      Logo en base64 para marca de agua (opcional)
 * @param {boolean} params.openInNewTab   true = abre vista previa; false = descarga
 */
export const generarContratoPDF = ({ formData, funciones = [], turnoDia = TURNO_DIA_DEFAULT, turnoNoche = TURNO_NOCHE_DEFAULT, logoBase64 = null, openInNewTab = false }) => {
    const d = formData;
    const esProvisorio = !!d.rutProvisorio;

    // Jornada: usa lo recibido del formulario; si viene vacío, cae a los valores por defecto
    const diaRows = (Array.isArray(turnoDia) && turnoDia.length) ? turnoDia : TURNO_DIA_DEFAULT;
    const nocheRows = (Array.isArray(turnoNoche) && turnoNoche.length) ? turnoNoche : TURNO_NOCHE_DEFAULT;

    const turnoDiaBody = [
        [{ text: 'TURNO DE DÍA  —  LUNES A SÁBADO', bold: true, alignment: 'center', fillColor: '#e9ecef' }],
        ...diaRows.map(linea => [{ text: linea, alignment: 'center' }])
    ];

    const turnoNocheBody = [
        [{ text: 'TURNO DE NOCHE  —  LUNES A VIERNES', colSpan: 3, bold: true, alignment: 'center', fillColor: '#e9ecef' }, {}, {}],
        [
            { text: 'Día trabajado', bold: true, alignment: 'center', fillColor: '#f1f3f5' },
            { text: 'Entrada', bold: true, alignment: 'center', fillColor: '#f1f3f5' },
            { text: 'Salida', bold: true, alignment: 'center', fillColor: '#f1f3f5' }
        ]
    ];
    nocheRows.forEach((row, i) => {
        turnoNocheBody.push([
            { text: String(i + 1), alignment: 'center' },
            { text: `${row.entranteDia || ''} ${row.entranteHora || ''}`.trim(), alignment: 'center' },
            { text: `${row.salienteDia || ''} ${row.salienteHora || ''}`.trim(), alignment: 'center' }
        ]);
    });

    const fechaContrato = d.fechaInicioContrato ? formatFecha(d.fechaInicioContrato) : "[Fecha Contrato]";
    const fechaInicio = d.fechaInicioContrato ? formatFecha(d.fechaInicioContrato) : "[Fecha Inicio]";
    const fechaFin = d.fechaFinContrato ? formatFecha(d.fechaFinContrato) : "[Fecha Fin]";

    // ----- Párrafo introductorio (cambia según rutProvisorio) -----
    const introStandard = [
        'En ', b(ph(d.ciudad, "Ciudad")), ', a ', b(fechaContrato), ', entre ',
        b(ph(d.empresa, "Empresa")), ', R.U.T ', b(ph(d.rutEmpresa, "RUT Empresa")),
        ', representada legalmente por don(a) ', b(ph(d.representanteLegal, "Representante Legal")),
        ', cédula de identidad ', b(ph(d.rutRepresentante, "RUT Representante")),
        ', ambos con domicilio en ', b(ph(d.domicilioEmpresa, "Domicilio Empresa")),
        ', en adelante el "Empleador" y don(a) ', b(ph(d.nombreTrabajador, "Nombre Trabajador")),
        ', Rut ', b(ph(formatRut(d.rutTrabajador), "RUT Trabajador")),
        ', nacionalidad ', b(ph(d.nacionalidad, "Nacionalidad")),
        ' nacido el ', b(d.fechaNacimiento ? formatFecha(d.fechaNacimiento) : "[Fecha Nacimiento]"),
        ', estado civil ', b(ph(d.estadoCivil, "Estado Civil")),
        ', Previsión ', b(ph(d.previsionSalud, "Previsión Salud")),
        ', Cotizante AFP ', b(ph(d.afp, "AFP")),
        ', domiciliado ', b(ph(d.domicilioTrabajador, "Domicilio Trabajador")),
        ', comuna de ', b(ph(d.comunaTrabajador, "Comuna Trabajador")),
        ', en adelante "Trabajador". Se ha convenido el siguiente CONTRATO DE TRABAJO:'
    ];

    const introProvisorio = [
        'En ', b(ph(d.ciudad, "Ciudad")), ', a ', b(fechaContrato), ', entre ',
        b(ph(d.empresa, "Empresa")), ', R.U.T ', b(ph(d.rutEmpresa, "RUT Empresa")),
        ', representada legalmente por don(a) ', b(ph(d.representanteLegal, "Representante Legal")),
        ', cédula de identidad ', b(ph(d.rutRepresentante, "RUT Representante")),
        ', ambos con domicilio en ', b(ph(d.domicilioEmpresa, "Domicilio Empresa")),
        ', en adelante el "Empleador" y don(a) ', b(ph(d.nombreTrabajador, "Nombre Trabajador")),
        ', DNI ', b(ph(d.rutTrabajador, "DNI Trabajador")),
        ', nacionalidad ', b(ph(d.nacionalidad, "Nacionalidad")),
        ' nacido el ', b(d.fechaNacimiento ? formatFecha(d.fechaNacimiento) : "[Fecha Nacimiento]"),
        ', estado civil ', b(ph(d.estadoCivil, "Estado Civil")),
        ', Previsión ', b(ph(d.previsionSalud, "Previsión Salud")),
        ', Cotizante AFP ', b(ph(d.afp, "AFP")),
        ', Rut Provisorio ', b(ph(formatRut(d.rutProvisorioNumero), "Rut Provisorio")),
        ', domiciliado ', b(ph(d.domicilioTrabajador, "Domicilio Trabajador")),
        ', comuna de ', b(ph(d.comunaTrabajador, "Comuna Trabajador")),
        ', en adelante "Trabajador". Se ha convenido el siguiente CONTRATO DE TRABAJO:'
    ];

    const funcionesList = funciones.map(f => ({ text: f, margin: [0, 2, 0, 2] }));

    // ----- Cláusulas adicionales para Rut Provisorio -----
    // Cuando hay rut provisorio se insertan dos cláusulas nuevas (DÉCIMO QUINTO y DÉCIMO SEXTO)
    // y se desplazan las siguientes a DÉCIMO SÉPTIMO y DÉCIMO OCTAVO.
    const clausulasFinales = esProvisorio
        ? {
            emails: 'DÉCIMO SÉPTIMO:',
            domicilio: 'DÉCIMO OCTAVO:'
        }
        : {
            emails: 'DÉCIMO QUINTO:',
            domicilio: 'DÉCIMO SEXTO:'
        };

    const clausulasProvisorioExtras = [
        // DÉCIMO QUINTO: Incumplimiento grave (Art. 160 extranjeros)
        { text: [bu('DÉCIMO QUINTO:'), ' Incumplimiento grave de las obligaciones que impone el contrato.'], style: 'sectionTitle', headlineLevel: 1 },
        { text: 'De acuerdo con lo establecido en el código del trabajo Causales del artículo 160:', style: 'paragraph' },
        { text: 'Se invocará esta causal dando término inmediato al contrato de trabajo siempre y cuando el trabajador de nacionalidad extranjera contratado no cumpla con la renovación a petición de su empleador de todo documento que haga referencia a complemento de situación migratoria, tales como, cédula de identidad vigente y/o pasaporte vigente, certificado de antecedentes penales apostillado de su país de origen, documento de autodenuncia PDI y firmas debidamente actualizadas, cumplir con un Rut provisorio cotizante en Chile, certificado de afiliación a salud chilena, todo lo anterior debe ser mensualmente renovado. Así mismo, el trabajador se compromete a realizar y concretar su proceso migratorio en Chile dentro de la vigencia de su contrato de trabajo, (NO SUPERIOR A 11 MESES) si no cumple con este requerimiento se dará término a la relación laboral.', style: 'paragraph' },

        // DÉCIMO SEXTO: Del viaje
        { text: [bu('DÉCIMO SEXTO:'), ' Del viaje.'], style: 'sectionTitle', headlineLevel: 1 },
        { text: 'El empleador no se compromete a pagar, al término de la relación laboral (ya sea por término de contrato, despido o renuncia), el pasaje de regreso del trabajador y los miembros de su familia que se estipulan, a su país de origen, ya que el trabajador se encontraba con residencia en el país al momento de ser contratado.', style: 'paragraph' },
    ];

    const docDefinition = {
        pageSize: 'LETTER',
        pageMargins: [40, 40, 40, 40],
        // Evita títulos de cláusula huérfanos: si un encabezado (headlineLevel 1)
        // quedó solo al final de la página sin contenido debajo, lo manda completo
        // a la página siguiente junto con su cláusula.
        pageBreakBefore: function (currentNode, followingNodesOnPage) {
            return currentNode.headlineLevel === 1 && followingNodesOnPage.length === 0;
        },
        background: function (currentPage, pageSize) {
            if (!logoBase64) return null;
            return {
                image: logoBase64,
                width: 450,
                opacity: 0.06,
                absolutePosition: {
                    x: (pageSize.width - 450) / 2,
                    y: (pageSize.height - 159) / 2
                }
            };
        },
        defaultStyle: { fontSize: 10, lineHeight: 1.3 },
        styles: {
            title: { fontSize: 16, bold: true, alignment: 'center', margin: [0, 0, 0, 20] },
            sectionTitle: { fontSize: 10, bold: true, margin: [0, 10, 0, 5] },
            paragraph: { fontSize: 10, alignment: 'justify', margin: [0, 5, 0, 5] },
            subItem: { fontSize: 10, margin: [15, 2, 0, 2] },
            signature: { fontSize: 10, alignment: 'center', margin: [0, 40, 0, 5] }
        },
        content: [
            { text: 'CONTRATO DE TRABAJO', style: 'title' },

            // Intro
            { text: esProvisorio ? introProvisorio : introStandard, style: 'paragraph' },

            // PRIMERO
            {
                text: [
                    bu('PRIMERO:'), ' El trabajador se compromete y obliga a prestar servicios como ',
                    b(ph(d.cargo, "Cargo")), ' u otro trabajo o función similar, que tenga directa relación con el cargo ya indicado, en las dependencias de ',
                    b(ph(d.lugarTrabajo, "Lugar Trabajo")), ', ', b(ph(d.direccionTrabajo, "Dirección Trabajo")),
                    ', comuna de ', b(ph(d.comunaTrabajo, "Comuna Trabajo")), ', en donde es enviado por ',
                    b(ph(d.empresa, "Empresa")),
                    ' a prestar los servicios para lo que es contratado. Pudiendo ser trasladado a otro departamento, cambiado de turno del original, sección de la oficina principal o de cualquiera de las agencias del Empleador, a condiciones que se trate de labores similares, y sin que ello importe menoscabo para el trabajador, todo ello sujeto a las necesidades operativas de la Empresa.'
                ], style: 'paragraph'
            },
            { text: 'Se deja establecido que el trabajador se regirá por el artículo 38 del Código del Trabajo, esto es, se encontrará exceptuado del descanso dominical y de días festivos, atendido a la naturaleza de las funciones que realiza al interior de la empresa. El trabajador tendrá derecho a los días de descanso compensatorio por los días domingo y festivos trabajados.', style: 'paragraph' },
            { text: 'Las funciones que el trabajador realizará son:', bold: true, margin: [0, 5, 0, 5] },
            { ul: funcionesList, margin: [20, 5, 0, 10] },

            // SEGUNDO
            { text: [bu('SEGUNDO:'), ' JORNADA DE TRABAJO'], style: 'sectionTitle', headlineLevel: 1 },
            {
                text: [
                    'El trabajador cumplirá una jornada semanal ordinaria de ', b(ph(d.horasSemanales, "Horas Semanales")),
                    ' horas, según necesidades de la Empresa, en las dependencias de ', b(ph(d.lugarTrabajo, "Lugar Trabajo")),
                    ', distribuida en los siguientes turnos rotativos:'
                ], style: 'paragraph'
            },

            // TURNO DE DÍA
            {
                table: { headerRows: 1, widths: ['*'], body: turnoDiaBody },
                layout: standardTableLayout,
                margin: [0, 10, 0, 5]
            },
            { text: 'La jornada de trabajo será interrumpida con un descanso de 60 minutos destinados a la colación, excepto y únicamente el día sábado.', style: 'paragraph' },

            // TURNO DE NOCHE (rotación)
            {
                table: { headerRows: 2, widths: ['*', '*', '*'], body: turnoNocheBody },
                layout: standardTableLayout,
                margin: [0, 5, 0, 5]
            },
            { text: 'La jornada de trabajo será interrumpida con un descanso de 60 minutos destinados a la colación.', style: 'paragraph' },

            // TERCERO
            { text: [bu('TERCERO:'), ' El trabajador no se encuentra autorizado para realizar horas extraordinarias, sin autorización del empleador.'], style: 'paragraph' },

            // CUARTO
            {
                text: [
                    bu('CUARTO:'), ' El empleador se compromete a remunerar los servicios del trabajador, los primeros 06 de cada mes vencido, con un sueldo base mensual de ',
                    b(`$${ph(d.sueldoBase, "Sueldo Base")} (${ph(d.sueldoTexto, "Sueldo en Texto")})`),
                    ' el cual se ', b('"reajustará en conformidad de la ley"'),
                    ' y será liquidado y pagado, por periodos vencidos y en forma proporcional a los días trabajados el siguiente beneficio:'
                ], style: 'paragraph'
            },
            { text: `Bono de movilización: Se otorgará un bono de ${ph(d.bonoMovilizacion, "Bono Movilización")} no imponibles (${ph(d.bonoMovilizacionTexto, "Bono en Texto")}) por día efectivamente trabajado.`, style: 'subItem', bold: true },
            { text: 'Las deducciones que la Empleadora podrá según los casos – practicar a las remuneraciones, son todas aquellas que disponen el Artículo 58 del Código del Trabajo.', style: 'paragraph' },

            // QUINTO
            { text: [bu('QUINTO:'), ' El trabajador, asimismo, acepta y autoriza al Empleador para que haga las deducciones que establecen las leyes vigentes y, para que le descuente el tiempo no trabajado debido a atrasos, inasistencias o permisos y, además, la rebaja del monto de las multas establecidas en el Reglamento Interno de Orden, Higiene y Seguridad, en caso que procedieren.'], style: 'paragraph' },
            { text: ['Los atrasos reiterados y las ausencias injustificadas darán lugar a la terminación inmediata del contrato de trabajo, sin derecho a indemnización alguna. Se entenderá por atraso el ingreso del trabajador(a) después de la hora señalada en su contrato de trabajo o en el correspondiente turno; y por atraso reiterado, la ocurrencia de 3 (tres) o más atrasos por semana y/o de 6 (seis) o más atrasos en un mes, que acumulen un total de 20 (veinte) minutos o más de atrasos en el periodo mensual. Título IX: DE LAS OBLIGACIONES Y PROHIBICIONES DEL REGLAMENTO INTERNO DE ', b(ph(d.empresa, "Empresa")), '.'], style: 'paragraph' },

            // SEXTO
            { text: [bu('SEXTO:'), ' La Empresa se obliga a pagar al empleador una gratificación anual equivalente al 25% (veinticinco por ciento) del total de las remuneraciones mensuales que este hubiese percibido en el año, con el tope de 4,75 Ingresos Mínimos. Esta gratificación se calculará, liquidará y anticipará mensualmente en forma coetánea con la remuneración del mes respectivo, siendo cada abono equivalente a la doceava parte de la gratificación anual. La gratificación así convenida es incompatible y sustituye a la que resulte de la aplicación de los artículos 74 y siguientes al Código del Trabajo. Para los efectos de cotejar la gratificación convenida en esta cláusula con la que, según la ley, eventualmente podría corresponder al Empleado, los valores anticipados mensualmente se reajustarán con conformidad con lo dispuesto en el artículo 63 del Código del Trabajo, y se entenderá que fueron abonados con carácter de anticipos de dichas gratificaciones legales. Con todo, si las sumas anticipadas a título de gratificación convencional resultaren mayores que las que legalmente correspondieren al Empleador, el exceso se consolidará en su beneficio.'], style: 'paragraph' },

            // SÉPTIMO
            { text: [bu('SÉPTIMO:'), ' REGISTRO DE ASISTENCIA'], style: 'sectionTitle', headlineLevel: 1 },
            { text: ['De acuerdo con el artículo N°21 para los efectos de controlar la asistencia y las horas de trabajo, tanto de la jornada ordinaria como la extraordinaria, los trabajadores y trabajadoras deberán registrar el ingreso y salida del lugar de trabajo por los medios manuales, mecánicos, magnéticos o de otra índole que ', b(ph(d.empresa, "Empresa")), ' determine. El registro se deberá hacer de forma inmediata al ingreso o egreso del trabajo y, para el caso del trabajador(a) con descanso con derecho a descanso entre la jornada, el registro también se debe hacer al salir y al regresar de la colación o descanso.'], style: 'paragraph' },

            // OCTAVO
            { text: [bu('OCTAVO:'), ' Regulación general sobre todo tipo de permisos:'], style: 'sectionTitle', headlineLevel: 1 },
            {
                ul: [
                    { text: 'Todo permiso que se solicite bajo cualquier circunstancia deberá estar respaldado por el respectivo comprobante de solicitud de permiso en uso en la empresa.', margin: [0, 2, 0, 2] },
                    { text: ['Deberá ser solicitado con la debida anticipación, salvo que en casos imprevistos sea imposible presentarlos con anticipación y ', bu('debidamente justificados.')], margin: [0, 2, 0, 2] },
                    { text: [bu('Ningún trabajador(a) podrá autorizar sus propios permisos,'), ' debiendo siempre solicitarlos y obtenerlos por conducto de su superior jerárquico.'], margin: [0, 2, 0, 2] },
                    { text: ['Todo permiso que se solicite por horas, durante y antes del inicio o término de la jornada de trabajo, serán autorizados directamente por el jefe correspondiente y ', bu('serán sin goce de sueldo.')], margin: [0, 2, 0, 2] },
                    { text: ['Se acuerda de mutuo acuerdo entre las partes que, en temporada de demanda alta de prestación de los servicios a la empresa mandante, tales descansos serán gestionados cuando ', bu('baje el nivel de demanda de la prestación del servicio a la empresa mandante.')], margin: [0, 2, 0, 2] },
                    { text: [bu('En caso fortuito o de enfermedad será otorgado en la temporada alta.')], margin: [0, 2, 0, 2] }
                ],
                margin: [20, 5, 0, 10]
            },

            // NOVENO
            { text: [bu('NOVENO:'), ' El trabajador se obliga y compromete expresamente a cumplir las instrucciones y funciones y/o labores a realizar dictadas en este contrato de trabajo además de las que le sean impartidas por su jefe inmediato o por la Gerencia de la Empresa y, acatar en todas sus partes las disposiciones establecidas en el Reglamento de Orden, Higiene y Seguridad las que declara conocer y que, para estos efectos se consideran parte integrante del presente contrato, reglamento del cual el trabajador recibe un ejemplar en este acto.'], style: 'paragraph' },

            // DÉCIMO
            { text: [bu('DÉCIMO:'), ' El trabajador acepta que se le hizo entrega de los EPP íntegros que serán para su cuidado y protección personal, los cuales deberá usar siempre, en todo momento mientras se mantenga operando en las instalaciones, así mismo las inducciones correspondientes por parte de ', b(ph(d.empresa, "Empresa")), ' y así mismo de la Empresa Mandante para el autocuidado y mutuo cuidado, para lo cual se anexa un compromiso de seguridad al trabajador; de no cumplir darán lugar a la terminación inmediata del contrato de trabajo, sin derecho a indemnización alguna.'], style: 'paragraph' },
            { text: 'El trabajador se compromete expresamente a cumplir y acatar todo tipo de instructivo de sistema de gestión de seguridad y salud ocupacional, que la mandante indique a la contratista, todo sea para velar por una oportuna seguridad en el trabajo. De no cumplir con este requerimiento el trabajador será notificado en primera instancia verbalmente dejando constancia laboral, luego amonestación formal y por último con copia a la inspección del trabajo, si su actuar es reiterativo será desvinculado según lo que establezca la ley.', style: 'paragraph' },
            { text: ['Dado que el Código del Trabajo (Art. 184) establece el ', b('"deber de cuidado"'), ' del empleador, ello implica la obligación del trabajador de acatar las medidas de prevención. Se dejan establecidas las obligaciones específicas del trabajador:'], style: 'paragraph' },
            {
                ul: [
                    { text: 'Normas de seguridad de la empresa.', margin: [0, 2, 0, 2] },
                    { text: 'Uso de Elementos de Protección Personal.', margin: [0, 2, 0, 2] },
                    { text: 'Asistencia diaria a las charlas de seguridad (como la charla de 5 minutos).', margin: [0, 2, 0, 2] },
                    { text: 'Intervenciones, charlas y capacitaciones que tengan relevancia para la seguridad en el trabajo, ya sea impartidas por el Prevencionista de Riesgos, supervisores, líderes o empresa mandante.', margin: [0, 2, 0, 2] }
                ],
                margin: [20, 5, 0, 10]
            },
            { text: ['El incumplimiento de las normas de seguridad de la empresa atiende al Artículo 160 N° 7 del Código del Trabajo: ', b('"Incumplimiento grave de las obligaciones que impone el contrato".')], style: 'paragraph' },

            // DÉCIMO PRIMERO
            { text: [bu('DÉCIMO PRIMERO:'), ' El presente contrato regirá con carácter de ', b(ph(d.tipoContrato, "Tipo Contrato")), ' desde el día ', b(fechaInicio), ' hasta el ', b(fechaFin), ', periodo que se denomina de ', b('"PRUEBA"'), ', con el fin de evaluar su desempeño, puntualidad y responsabilidad; pasada la evaluación correspondiente, este contrato pasará a ser automáticamente por 3 meses contados desde la fecha de ingreso. Cualquiera de las partes, o ambas, según el caso, podrán ponerle término en cualquier momento con arreglo a la ley.'], style: 'paragraph' },

            // DÉCIMO SEGUNDO
            { text: [bu('DÉCIMO SEGUNDO:'), ' Se deja constancia que ', b(ph(d.empresa, "Empresa")), ', actúa con el giro de otras actividades de servicios de apoyo de personas a las empresas, por lo que, ', bu('si la empresa mandante solicita la desvinculación del trabajador por motivos propios, se pondrá término a la relación laboral'), ' y se le hará conocer los motivos por los cuales fue desvinculado.'], style: 'paragraph' },

            // DÉCIMO TERCERO
            { text: [bu('DÉCIMO TERCERO:'), ' CONFIDENCIALIDAD'], style: 'sectionTitle', headlineLevel: 1 },
            { text: 'El trabajador se obliga en forma irrevocable a que toda Información que LA EMPRESA le haya proporcionado o le proporcione con motivo de este Contrato. Tendrá el carácter de confidencial, cualquiera sea la forma o formato a través del cual se exprese dicha información, sea verbalmente o por escrito, sea que se contenga en documentos, memoranda, escritos de otra naturaleza, discos, cintas, disquetes, archivos computacionales o en cualquiera otra forma. El trabajador no divulgará, publicará ni permitirá la publicación de todo o parte de la Información Confidencial, ni ponerla a disposición de terceros, a no ser que cuente con el consentimiento explícito de LA EMPRESA. Al momento de la resolución por cualquier causa de este Contrato, el trabajador deberá devolver, según lo indique el empleador, la Información Confidencial y toda copia, resumen o extracto de ésta, contenida en cualquier documento de trabajo, memorandos u otros escritos, discos, cintas, disquetes o archivos computacionales, sin retener copias, resúmenes o extractos de la misma, en ninguna forma.', style: 'paragraph' },

            // DÉCIMO CUARTO
            { text: [bu('DÉCIMO CUARTO:'), ' Del régimen previsional. Se deja constancia que el Trabajador cotiza en el régimen previsional chileno, comprometiéndose al Empleador a efectuar las retenciones y entregarlas a las instituciones correspondientes.'], style: 'paragraph' },

            // Cláusulas extras sólo si Rut Provisorio (DÉCIMO QUINTO y DÉCIMO SEXTO adicionales)
            ...(esProvisorio ? clausulasProvisorioExtras : []),

            // Emails (DÉCIMO QUINTO estándar / DÉCIMO SÉPTIMO provisorio)
            { text: [bu(clausulasFinales.emails), ' Las partes establecen los siguientes correos electrónicos para efectos de comunicaciones y avisos que deban realizarse por medios digitales:'], style: 'paragraph' },
            { text: ['- Empleador: ', b(ph(d.emailEmpleador, "Email Empleador")), '  -  Trabajador: ', b(ph(d.emailTrabajador, "Email Trabajador"))], style: 'subItem' },

            // Domicilio (DÉCIMO SEXTO estándar / DÉCIMO OCTAVO provisorio)
            { text: [bu(clausulasFinales.domicilio), ' Para todas las cuestiones a que eventualmente pueda dar origen este contrato, las partes fijan domicilio en la comuna de ', b(ph(d.ciudad, "Ciudad")), '.'], style: 'paragraph' },

            // Firmas (bloque indivisible: el cierre y las firmas no se parten entre páginas)
            {
                unbreakable: true,
                stack: [
                    { text: 'El presente contrato de trabajo se firma en tres ejemplares en este mismo acto uno en poder de cada contratante.', style: 'paragraph' },
                    {
                        margin: [0, 50, 0, 0],
                        columns: [
                    {
                        width: '*',
                        stack: [
                            { text: '________________________', alignment: 'center' },
                            { text: ph(d.nombreTrabajador, "Nombre Trabajador"), alignment: 'center', bold: true, margin: [0, 5, 0, 0], fontSize: 9 },
                            esProvisorio
                                ? { text: `Rut Provisorio ${ph(formatRut(d.rutProvisorioNumero), "Rut Provisorio")}`, alignment: 'center', fontSize: 9 }
                                : { text: `RUT ${ph(formatRut(d.rutTrabajador), "RUT Trabajador")}`, alignment: 'center', fontSize: 9 }
                        ]
                    },
                    {
                        width: '*',
                        stack: [
                            { text: '________________________', alignment: 'center' },
                            { text: ph(d.empresa, "Empresa"), alignment: 'center', bold: true, margin: [0, 5, 0, 0], fontSize: 9 },
                            { text: ph(d.rutEmpresa, "RUT Empresa"), alignment: 'center', fontSize: 9 },
                            { text: `PP. ${ph(d.representanteLegal, "Representante Legal")}`, alignment: 'center', fontSize: 9 }
                        ]
                    }
                        ]
                    }
                ]
            }
        ]
    };

    if (openInNewTab) {
        pdfMake.createPdf(docDefinition).open();
    } else {
        const nombreArchivo = d.nombreTrabajador
            ? `Contrato_${d.nombreTrabajador.replace(/\s+/g, '_')}.pdf`
            : 'Contrato_Trabajo.pdf';
        pdfMake.createPdf(docDefinition).download(nombreArchivo);
    }
};
